import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { getData, postData } from '../../webService/webservice.js';
import { toast } from 'react-toastify';

/**
 * Helper to convert numbers to Indian Rupee Words
 */
const numberToWordsINR = (num) => {
    if (isNaN(num) || num === null || num === undefined) return 'Zero';
    const amount = Math.round(Number(num));
    if (amount === 0) return 'Indian Rupee Zero Only';

    const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const inWords = (n) => {
        const strVal = n.toString();
        if (strVal.length > 9) return 'Overflow';
        const n_array = ('000000000' + strVal).slice(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
        if (!n_array) return '';
        let str = '';
        str += (Number(n_array[1]) !== 0) ? (a[Number(n_array[1])] || b[n_array[1][0]] + ' ' + a[n_array[1][1]]) + 'Crore ' : '';
        str += (Number(n_array[2]) !== 0) ? (a[Number(n_array[2])] || b[n_array[2][0]] + ' ' + a[n_array[2][1]]) + 'Lakh ' : '';
        str += (Number(n_array[3]) !== 0) ? (a[Number(n_array[3])] || b[n_array[3][0]] + ' ' + a[n_array[3][1]]) + 'Thousand ' : '';
        str += (Number(n_array[4]) !== 0) ? (a[Number(n_array[4])] || b[n_array[4][0]] + ' ' + a[n_array[4][1]]) + 'Hundred ' : '';
        str += (Number(n_array[5]) !== 0) ? ((str !== '') ? 'and ' : '') + (a[Number(n_array[5])] || b[n_array[5][0]] + ' ' + a[n_array[5][1]]) : '';
        return str.trim();
    };

    const words = inWords(amount);
    return `Indian Rupee ${words} Only`;
};

/**
 * Format Date as DD/MM/YYYY
 */
const formatDateDDMMYYYY = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) return dateInput;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (e) {
        return dateInput;
    }
};

/**
 * Format Subject dynamically based on Package Name/Type
 */
const getSubjectText = (invoice) => {
    if (invoice.subject) return invoice.subject;
    const pkg = (invoice.packageName || '').toUpperCase();
    if (pkg.includes('YEARLY') || pkg.includes('ANNUAL')) {
        return 'TVFM POS Software promotional Annual Package';
    }
    if (pkg.includes('TRIAL')) {
        return 'TVFM POS Software promotional Trial Package';
    }
    if (pkg.includes('HALF YEARLY') || pkg.includes('HALF-YEARLY')) {
        return 'TVFM POS Software promotional Half Yearly Package';
    }
    if (pkg.includes('QUARTERLY')) {
        return 'TVFM POS Software promotional Quarterly Package';
    }
    if (pkg.includes('MONTHLY')) {
        return 'TVFM POS Software promotional Monthly Package';
    }
    const cleanName = (invoice.packageName || 'Package').replace(/PACKAGE|PACK/gi, '').trim();
    return `TVFM POS Software promotional ${cleanName ? cleanName + ' ' : ''}Package`;
};

/**
 * Format Item & Description dynamically based on Package Name/Type
 */
const getItemDescription = (invoice) => {
    const pkg = (invoice.packageName || 'YEARLY PACKAGE').toUpperCase().trim();
    let line1 = invoice.itemTitle || 'SMART SALON POS';
    let line2 = invoice.softwareName || 'TVFM SALON SOFTWARE';
    let line3 = `${pkg} SUBSCRIPTION`;

    if (pkg.includes('YEARLY') || pkg.includes('ANNUAL')) {
        line1 = invoice.itemTitle || 'SMART SALON POS YLY';
        line3 = 'YEARLY SUBSCRIPTION';
    } else if (pkg.includes('TRIAL')) {
        line1 = invoice.itemTitle || 'SMART SALON POS';
        if (pkg.includes('THREE MONTH') || pkg.includes('3 MONTH')) {
            line3 = 'THREE MONTH TRIAL SUBSCRIPTION';
        } else {
            line3 = `${pkg} SUBSCRIPTION`.replace('PACK SUBSCRIPTION', 'TRIAL SUBSCRIPTION');
            if (!line3.includes('TRIAL')) line3 = 'TRIAL SUBSCRIPTION';
        }
    } else if (pkg.includes('HALF YEARLY') || pkg.includes('HALF-YEARLY')) {
        line1 = invoice.itemTitle || 'SMART SALON POS';
        line3 = 'HALF YEARLY SUBSCRIPTION';
    } else if (pkg.includes('QUARTERLY')) {
        line1 = invoice.itemTitle || 'SMART SALON POS';
        line3 = 'QUARTERLY SUBSCRIPTION';
    } else if (pkg.includes('MONTHLY')) {
        line1 = invoice.itemTitle || 'SMART SALON POS';
        line3 = 'MONTHLY SUBSCRIPTION';
    } else {
        line1 = invoice.itemTitle || 'SMART SALON POS';
        line3 = `${pkg} SUBSCRIPTION`.replace('PACKAGE SUBSCRIPTION', 'SUBSCRIPTION').replace('PACK SUBSCRIPTION', 'SUBSCRIPTION');
    }

    return { line1, line2, line3 };
};

/**
 * Format Terms & Conditions Validity dynamically
 */
const getTermsValidity = (invoice) => {
    const pkg = (invoice.packageName || '').toUpperCase();
    const subStart = formatDateDDMMYYYY(invoice.subscriptionStartDate || invoice.invoiceDate || invoice.createdAt || new Date());

    let label = 'Subscription package';
    if (pkg.includes('YEARLY') || pkg.includes('ANNUAL')) {
        label = 'Annual package';
    } else if (pkg.includes('TRIAL')) {
        label = 'Trial package';
    } else if (pkg.includes('HALF YEARLY') || pkg.includes('HALF-YEARLY')) {
        label = 'Half yearly package';
    } else if (pkg.includes('QUARTERLY')) {
        label = 'Quarterly package';
    } else if (pkg.includes('MONTHLY')) {
        label = 'Monthly package';
    }

    const validityLine = `${label} valid from ${subStart}.`;

    let extraLine = '';
    const extraVal = Number(invoice.extraValidity || 0);
    const extraUnit = (invoice.extraValidityUnit || 'Months').toLowerCase();

    if (extraVal > 0) {
        const numWords = {
            1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve'
        };
        const wordVal = numWords[extraVal] || String(extraVal);
        const capitalizedWord = wordVal.charAt(0).toUpperCase() + wordVal.slice(1);
        const unitDisplay = extraVal === 1 ? extraUnit.replace(/s$/, '') : extraUnit;
        extraLine = `${capitalizedWord} ${unitDisplay} extra access free.`;
    } else if (invoice.extraValidityText) {
        extraLine = invoice.extraValidityText;
    }

    return { validityLine, extraLine };
};

/**
 * Generate Printable HTML String matching exact Tax Invoice Layout
 */
export const generatePackageInvoicePrintHTML = (invoice) => {
    if (!invoice) return '';

    const invNo = invoice.invoiceNumber || 'PKG-INV-20260924-0001';
    const invDate = formatDateDDMMYYYY(invoice.invoiceDate || invoice.createdAt || new Date());
    const dueDate = formatDateDDMMYYYY(invoice.dueDate || invoice.invoiceDate || invoice.createdAt || new Date());

    const storeName = (invoice.storeName || 'Store').toUpperCase();
    const branchName = invoice.storeBranchName ? ` (${invoice.storeBranchName})` : '';
    const storeAddress = invoice.storeAddress || '';
    const storePhone = invoice.storePhone || '';
    const storeEmail = invoice.storeEmail || '';
    const storeGst = invoice.storeGstNo || '';
    const placeOfSupply = invoice.placeOfSupply || 'Tamil Nadu (33)';

    const subjectText = getSubjectText(invoice);
    const itemDesc = getItemDescription(invoice);
    const hsnSac = invoice.hsnSac || invoice.hsnCode || '997331';
    const termsValidity = getTermsValidity(invoice);

    // Pricing & Tax Calculation
    const pkgPrice = Number(invoice.packagePrice || 0);
    const isGst = invoice.isGstApplicable !== undefined ? Boolean(invoice.isGstApplicable) : true;
    const gstType = invoice.gstType || 'Exclusive';
    const gstPct = Number(invoice.gstPercentage || 18);
    const rawGstAmt = Number(invoice.gstAmount || 0);

    let baseRate = pkgPrice;
    let totalTax = rawGstAmt > 0 ? rawGstAmt : (pkgPrice * gstPct) / 100;
    let totalAmt = Number(invoice.totalAmount || (baseRate + totalTax));

    if (isGst && gstType === 'Inclusive' && rawGstAmt === 0) {
        totalAmt = pkgPrice;
        totalTax = pkgPrice - (pkgPrice / (1 + gstPct / 100));
        baseRate = totalAmt - totalTax;
    } else if (!isGst) {
        totalTax = 0;
        totalAmt = pkgPrice;
        baseRate = pkgPrice;
    }

    const halfGstPct = (gstPct / 2).toFixed(1).replace(/\.0$/, '');
    const halfGstAmt = totalTax / 2;

    const formatINR = (num) => {
        const val = Number(num || 0);
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Rounding offset calculation
    const calculatedBasePlusTax = baseRate + totalTax;
    let roundVal = totalAmt - calculatedBasePlusTax;
    if (Math.abs(roundVal) < 0.0001 && invoice.totalAmount && Math.round(totalAmt) !== totalAmt) {
        totalAmt = Math.round(totalAmt);
        roundVal = totalAmt - calculatedBasePlusTax;
    }
    const roundingStr = Math.abs(roundVal) > 0.0001 ? roundVal.toFixed(2) : '0.18';

    const paymentMade = invoice.paymentMade !== undefined ? Number(invoice.paymentMade) : totalAmt;
    const balanceDue = Number(invoice.balanceDue || 0);

    const totalInWords = numberToWordsINR(totalAmt);

    const logoUrl = invoice.logoUrl || (typeof window !== 'undefined' ? `${window.location.origin}/assets/tvfmlogo.png` : '/assets/tvfmlogo.png');
    const signatureSrc = invoice.signatureImage || invoice.authorizedSignature || (typeof window !== 'undefined' ? `${window.location.origin}/assets/image__11_-removebg-preview.png` : '/assets/image__11_-removebg-preview.png');

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Tax Invoice - ${invNo}</title>
    <style>
        @page {
            size: A4 portrait;
            margin: 8mm;
        }
        * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
            font-family: Arial, Helvetica, sans-serif;
            -webkit-print-color-adjust: exact;
        }
        body {
            background-color: #fff;
            color: #000;
            font-size: 10px;
            line-height: 1.3;
            padding: 10px;
        }
        .invoice-outer-box {
            border: 1px solid #333;
            width: 100%;
            margin: 0 auto;
            background: #fff;
        }

        @media print {
            html, body {
                margin: 0 !important;
                padding: 0 !important;
            }
            .invoice-outer-box {
                border: 1px solid #333 !important;
            }
        }
        
        /* 1. Header Table */
        .header-table {
            width: 100%;
            border-collapse: collapse;
            border-bottom: 1px solid #333;
        }
        .header-table td {
            padding: 10px 12px;
            vertical-align: top;
        }
        .logo-cell {
            width: 210px;
            vertical-align: middle;
        }
        .logo-img {
            max-width: 195px;
            max-height: 85px;
            width: auto;
            height: auto;
            object-fit: contain;
            display: block;
        }
        .company-cell {
            padding-left: 5px;
        }
        .company-name {
            font-size: 15px;
            font-weight: bold;
            color: #000;
            margin-bottom: 2px;
            letter-spacing: 0.3px;
        }
        .company-info {
            font-size: 9.5px;
            color: #000;
            line-height: 1.35;
        }
        .title-cell {
            width: 180px;
            vertical-align: middle;
            text-align: right;
        }
        .tax-invoice-title {
            font-size: 22px;
            font-weight: bold;
            color: #000;
            letter-spacing: 0.5px;
        }

        /* 2. Metadata Table */
        .meta-table {
            width: 100%;
            border-collapse: collapse;
            border-bottom: 1px solid #333;
        }
        .meta-table td {
            padding: 4px 12px;
            vertical-align: top;
            font-size: 9.5px;
        }
        .meta-left-cell {
            width: 58%;
            border-right: 1px solid #333;
        }
        .meta-right-cell {
            width: 42%;
        }
        .meta-row-table {
            width: 100%;
            border-collapse: collapse;
        }
        .meta-row-table td {
            padding: 1.5px 0;
            border: none;
            font-size: 9.5px;
        }
        .meta-lbl {
            width: 100px;
            font-weight: normal;
            color: #000;
        }
        .meta-colon {
            width: 15px;
        }
        .meta-val {
            font-weight: bold;
            color: #000;
        }

        /* 3. Bill To Table */
        .billto-table {
            width: 100%;
            border-collapse: collapse;
            border-bottom: 1px solid #333;
        }
        .billto-table td {
            padding: 8px 12px;
            font-size: 9.5px;
            line-height: 1.4;
        }
        .billto-title {
            font-weight: bold;
            color: #000;
            margin-bottom: 4px;
        }
        .customer-name {
            font-weight: bold;
            font-size: 11px;
            color: #000;
            margin-bottom: 2px;
        }

        /* 4. Subject Table */
        .subject-table {
            width: 100%;
            border-collapse: collapse;
            border-bottom: 1px solid #333;
        }
        .subject-table td {
            padding: 6px 12px;
            font-size: 9.5px;
            color: #000;
        }

        /* 5. Items Table */
        .items-table {
            width: 100%;
            border-collapse: collapse;
            border-bottom: 1px solid #333;
        }
        .items-table th, .items-table td {
            border-right: 1px solid #333;
            border-bottom: 1px solid #333;
            padding: 5px 6px;
            font-size: 9px;
            vertical-align: top;
        }
        .items-table th {
            background-color: #fff;
            font-weight: bold;
            text-align: center;
        }
        .items-table th:last-child, .items-table td:last-child {
            border-right: none;
        }
        .items-table tr:last-child td {
            border-bottom: none;
        }

        /* 6. Bottom Split Section */
        .bottom-table {
            width: 100%;
            border-collapse: collapse;
        }
        .bottom-left-cell {
            width: 58%;
            border-right: 1px solid #333;
            padding: 8px 12px;
            vertical-align: top;
        }
        .bottom-right-cell {
            width: 42%;
            padding: 8px 12px;
            vertical-align: top;
        }

        .sec-heading {
            font-weight: bold;
            font-size: 9.5px;
            margin-bottom: 3px;
            color: #000;
        }
        .sec-body {
            font-size: 9px;
            line-height: 1.35;
            margin-bottom: 10px;
            color: #000;
        }

        .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 2px 0;
            font-size: 9.5px;
        }
        .summary-row.total-row {
            font-weight: bold;
            font-size: 10.5px;
            border-top: 1px solid #333;
            border-bottom: 1px solid #333;
            padding: 4px 0;
            margin: 4px 0;
        }
        .payment-made-val {
            color: #c92a2a;
            font-weight: bold;
        }

        .signature-box {
            margin-top: 25px;
            text-align: center;
            float: right;
            width: 170px;
        }
        .signature-img {
            max-width: 160px;
            max-height: 55px;
            width: auto;
            height: auto;
            object-fit: contain;
            display: block;
            margin: 0 auto 2px auto;
        }
        .signature-label {
            font-size: 9px;
            color: #000;
            font-weight: normal;
        }
    </style>
</head>
<body>
    <div class="invoice-outer-box">
        <!-- Top Header -->
        <table class="header-table">
            <tr>
                <td class="logo-cell">
                    <img src="${logoUrl}" alt="TVFM Logo" class="logo-img" />
                </td>
                <td class="company-cell">
                    <div class="company-name">TVFM SYNERGY LLP</div>
                    <div class="company-info">
                        Company ID : ACF-2710<br/>
                        NO. 91, VGN OVAL GARDEN, VANAGRAM MAIN ROAD,<br/>
                        ATHIPET, AMBATTUR, CHENNAI<br/>
                        Tiruvallur Tamil Nadu 600058<br/>
                        India<br/>
                        GSTIN 33AAVFT8068J1ZC<br/>
                        91-9940658115<br/>
                        tvfmsynergyllp@tvfmsynergy.com<br/>
                        www.tvfmsynergy.com
                    </div>
                </td>
                <td class="title-cell">
                    <div class="tax-invoice-title">TAX INVOICE</div>
                </td>
            </tr>
        </table>

        <!-- Metadata Section -->
        <table class="meta-table">
            <tr>
                <td class="meta-left-cell">
                    <table class="meta-row-table">
                        <tr>
                            <td class="meta-lbl">#</td>
                            <td class="meta-colon">:</td>
                            <td class="meta-val">${invNo}</td>
                        </tr>
                        <tr>
                            <td class="meta-lbl">Invoice Date</td>
                            <td class="meta-colon">:</td>
                            <td class="meta-val">${invDate}</td>
                        </tr>
                        <tr>
                            <td class="meta-lbl">Terms</td>
                            <td class="meta-colon">:</td>
                            <td class="meta-val">${invoice.terms || 'Due on Receipt'}</td>
                        </tr>
                        <tr>
                            <td class="meta-lbl">Due Date</td>
                            <td class="meta-colon">:</td>
                            <td class="meta-val">${dueDate}</td>
                        </tr>
                    </table>
                </td>
                <td class="meta-right-cell">
                    <table class="meta-row-table">
                        <tr>
                            <td class="meta-lbl">Place Of Supply</td>
                            <td class="meta-colon">:</td>
                            <td class="meta-val">${placeOfSupply}</td>
                        </tr>
                    </table>
                </td>
            </tr>
        </table>

        <!-- Bill To Section -->
        <table class="billto-table">
            <tr>
                <td>
                    <div class="billto-title">Bill To</div>
                    <div class="customer-name">${storeName}${branchName}</div>
                    ${storeAddress ? `<div>${storeAddress}</div>` : ''}
                    ${storePhone ? `<div>Phone: ${storePhone}</div>` : ''}
                    ${storeEmail ? `<div>Email: ${storeEmail}</div>` : ''}
                    ${storeGst ? `<div>GSTIN ${storeGst}</div>` : ''}
                </td>
            </tr>
        </table>

        <!-- Subject Section -->
        <table class="subject-table">
            <tr>
                <td>
                    <strong>Subject :</strong><br/>
                    <strong>${subjectText}</strong>
                </td>
            </tr>
        </table>

        <!-- Items Table -->
        <table class="items-table">
            <thead>
                <tr>
                    <th rowspan="2" style="width: 3%; text-align: center;">#</th>
                    <th rowspan="2" style="width: 33%; text-align: left;">Item & Description</th>
                    <th rowspan="2" style="width: 10%; text-align: center;">HSN<br/>/SAC</th>
                    <th rowspan="2" style="width: 8%; text-align: center;">Qty</th>
                    <th rowspan="2" style="width: 10%; text-align: right;">Rate</th>
                    <th colspan="2" style="text-align: center;">CGST</th>
                    <th colspan="2" style="text-align: center;">SGST</th>
                    <th rowspan="2" style="width: 12%; text-align: right;">Amount</th>
                </tr>
                <tr>
                    <th style="width: 5%; text-align: center;">%</th>
                    <th style="width: 9.5%; text-align: right;">Amt</th>
                    <th style="width: 5%; text-align: center;">%</th>
                    <th style="width: 9.5%; text-align: right; border-right: 1px solid #333;">Amt</th>
                </tr>
            </thead>
            <tbody>
                <tr style="height: 100px;">
                    <td style="text-align: center;">1</td>
                    <td>
                        <div style="font-weight: bold; font-size: 9.5px;">${itemDesc.line1}</div>
                        <div style="font-size: 9px; color: #111;">${itemDesc.line2}</div>
                        <div style="font-size: 9px; color: #111;">${itemDesc.line3}</div>
                    </td>
                    <td style="text-align: center;">${hsnSac}</td>
                    <td style="text-align: center;">
                        1.00<br/>
                        <span style="font-size: 8px; color: #333;">USER</span>
                    </td>
                    <td style="text-align: right;">${formatINR(baseRate)}</td>
                    <td style="text-align: center;">${isGst ? `${halfGstPct}%` : '-'}</td>
                    <td style="text-align: right;">${isGst ? `${formatINR(halfGstAmt)}` : '-'}</td>
                    <td style="text-align: center;">${isGst ? `${halfGstPct}%` : '-'}</td>
                    <td style="text-align: right;">${isGst ? `${formatINR(halfGstAmt)}` : '-'}</td>
                    <td style="text-align: right;">${formatINR(baseRate)}</td>
                </tr>
            </tbody>
        </table>

        <!-- Summary & Notes Dual Section -->
        <table class="bottom-table">
            <tr>
                <td class="bottom-left-cell">
                    <div class="sec-heading">Total In Words</div>
                    <div class="sec-body" style="font-style: italic; font-weight: bold;">${totalInWords}</div>

                    <div class="sec-heading" style="margin-top: 10px;">Notes</div>
                    <div class="sec-body">
                        Looking forward for your business.<br/>
                        --------------------------------<br/>
                        Payment received in full. Thank you for your support!!
                    </div>

                    <div class="sec-heading" style="margin-top: 10px;">Terms & Conditions</div>
                    <div class="sec-body">
                        As per Software usage agreement.<br/>
                        ${termsValidity.validityLine}${termsValidity.extraLine ? `<br/>${termsValidity.extraLine}` : ''}
                    </div>

                    <div style="margin-top: 20px; font-size: 9px; color: #000;">
                        This is a computer generated invoice. E. & O.E.
                    </div>
                </td>
                <td class="bottom-right-cell">
                    <div class="summary-row">
                        <span>Sub Total</span>
                        <span>${formatINR(baseRate)}</span>
                    </div>
                    ${isGst ? `
                    <div class="summary-row">
                        <span>CGST9 (${halfGstPct}%)</span>
                        <span>${formatINR(halfGstAmt)}</span>
                    </div>
                    <div class="summary-row">
                        <span>SGST9 (${halfGstPct}%)</span>
                        <span>${formatINR(halfGstAmt)}</span>
                    </div>
                    ` : ''}
                    <div class="summary-row">
                        <span>Rounding</span>
                        <span>${roundingStr}</span>
                    </div>
                    <div class="summary-row total-row">
                        <span>Total</span>
                        <span>₹${formatINR(totalAmt)}</span>
                    </div>
                    <div class="summary-row">
                        <span>Payment Made</span>
                        <span class="payment-made-val">(-) ${formatINR(paymentMade)}</span>
                    </div>
                    <div class="summary-row" style="font-weight: bold; font-size: 10px; border-top: 1px solid #555; padding-top: 3px; margin-top: 3px;">
                        <span>Balance Due</span>
                        <span>₹${formatINR(balanceDue)}</span>
                    </div>

                    <div class="signature-box">
                        <img src="${signatureSrc}" class="signature-img" alt="Authorized Signature" />
                        <div class="signature-label">Authorized Signature</div>
                    </div>
                </td>
            </tr>
        </table>
    </div>
</body>
</html>
    `;
};

/**
 * Handle direct print via iframe
 */
export const printPackageInvoiceDirect = (invoiceData) => {
    try {
        const html = generatePackageInvoicePrintHTML(invoiceData);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(html);
        doc.close();

        iframe.contentWindow.focus();
        setTimeout(() => {
            iframe.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 500);
    } catch (err) {
        console.error("Failed to print package invoice:", err);
        toast.error("Failed to trigger printer dialog");
    }
};

/**
 * Package Invoice Modal Component
 */
const PackageInvoiceModal = ({ isOpen, onClose, invoiceData, subscriptionId, invoiceId }) => {
    const [invoice, setInvoice] = useState(invoiceData || null);
    const [loading, setLoading] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);

    const fetchInvoice = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("Token");
            let endpoint = '';
            if (invoiceId) {
                endpoint = `package-invoices/${invoiceId}`;
            } else if (subscriptionId) {
                endpoint = `package-invoices/subscription/${subscriptionId}`;
            }

            if (endpoint) {
                const res = await getData(endpoint, {}, token);
                if ((res?.success || res?.status === 200) && res?.data) {
                    setInvoice(res.data);
                } else {
                    toast.error("Invoice record not found");
                }
            }
        } catch (err) {
            console.error("Error loading invoice:", err);
            toast.error("Failed to load invoice details");
        } finally {
            setLoading(false);
        }
    }, [invoiceId, subscriptionId]);

    useEffect(() => {
        if (invoiceData) {
            setInvoice(invoiceData);
        } else if (isOpen && (subscriptionId || invoiceId)) {
            fetchInvoice();
        }
    }, [isOpen, invoiceData, subscriptionId, invoiceId, fetchInvoice]);

    const handleSendEmail = async () => {
        if (!invoice) {
            toast.error("Invoice data is not ready to send email");
            return;
        }

        const targetId = invoice._id || invoice.id || invoice.invoiceNumber || invoiceId || subscriptionId;
        if (!targetId) {
            toast.error("Invoice ID is missing");
            return;
        }

        try {
            setSendingEmail(true);
            const token = localStorage.getItem("Token");
            const res = await postData(`package-invoices/${targetId}/send-email`, {}, token);

            if (res?.success || res?.status === 200) {
                toast.success(res?.message || "Package invoice email sent successfully!");
                setInvoice(prev => prev ? { ...prev, emailSent: true } : prev);
            } else {
                toast.error(res?.message || "Failed to send invoice email");
            }
        } catch (err) {
            console.error("Error sending invoice email:", err);
            toast.error(err.message || "Error sending invoice email");
        } finally {
            setSendingEmail(false);
        }
    };

    if (!isOpen) return null;

    const handlePrint = () => {
        if (invoice) {
            printPackageInvoiceDirect(invoice);
        } else {
            toast.error("Invoice data is not ready to print");
        }
    };

    const printHTMLContent = invoice ? generatePackageInvoicePrintHTML(invoice) : '';

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(5px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '20px'
        }} onClick={onClose}>
            <div style={{
                backgroundColor: '#ffffff', borderRadius: '12px',
                width: '100%', maxWidth: '860px', maxHeight: '92vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                overflow: 'hidden'
            }} onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div style={{
                    padding: '16px 24px', background: '#3c0008', color: '#ffffff',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Icon icon="solar:bill-check-bold-duotone" style={{ fontSize: '26px' }} />
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Package Tax Invoice Preview</h3>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'transparent', border: 'none', color: '#ffffff',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: '24px'
                    }}>
                        <Icon icon="solar:close-circle-bold-duotone" />
                    </button>
                </div>

                {/* Modal Body - Render Real Preview */}
                <div style={{ padding: '20px', overflowY: 'auto', flex: 1, backgroundColor: '#525659' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', background: '#fff', borderRadius: '8px' }}>
                            <Icon icon="line-md:loading-twotone-loop" style={{ fontSize: '42px', color: '#3c0008' }} />
                            <p style={{ marginTop: '12px', color: '#64748b', fontWeight: 600 }}>Loading Invoice Document...</p>
                        </div>
                    ) : invoice ? (
                        <div style={{
                            background: '#ffffff', padding: '10px', borderRadius: '4px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)', margin: '0 auto'
                        }}>
                            <iframe
                                title="Invoice Preview"
                                srcDoc={printHTMLContent}
                                style={{
                                    width: '100%',
                                    height: '680px',
                                    border: 'none',
                                    backgroundColor: '#ffffff'
                                }}
                            />
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '60px 0', background: '#fff', borderRadius: '8px', color: '#94a3b8' }}>
                            No invoice data available.
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div style={{
                    padding: '16px 24px', background: '#ffffff', borderTop: '1px solid #e2e8f0',
                    display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center'
                }}>
                    <button onClick={onClose} style={{
                        padding: '10px 20px', borderRadius: '6px', border: '1px solid #cbd5e1',
                        background: '#ffffff', color: '#475569', fontWeight: 600, cursor: 'pointer'
                    }}>
                        Close
                    </button>

                    <button onClick={handleSendEmail} disabled={!invoice || sendingEmail} style={{
                        padding: '10px 20px', borderRadius: '6px', border: '1px solid #cbd5e1',
                        background: '#f8fafc', color: '#0f172a', fontWeight: 600, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px', opacity: (invoice && !sendingEmail) ? 1 : 0.6
                    }}>
                        <Icon icon="solar:letter-bold-duotone" style={{ fontSize: '18px', color: '#3c0008' }} />
                        {sendingEmail ? "Sending..." : "Send Email"}
                    </button>

                    <button onClick={handlePrint} disabled={!invoice} style={{
                        padding: '10px 24px', borderRadius: '6px', border: 'none',
                        background: '#3c0008', color: '#ffffff', fontWeight: 700, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px', opacity: invoice ? 1 : 0.6
                    }}>
                        <Icon icon="solar:printer-bold-duotone" style={{ fontSize: '18px' }} />
                        Print Tax Invoice
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PackageInvoiceModal;

